import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';
import axios from 'axios';

// ─── Shared constants ─────────────────────────────────────────────────────────


const OXFORD_GENDER_OPTIONS = [
  { value: '',   label: 'All' },
  { value: 'M',  label: "Men's" },
  { value: 'W',  label: "Women's" },
  { value: 'WC', label: "Women's w/ Children" },
  { value: 'MC', label: "Men's w/ Children" },
];

const SOBER_GENDER_OPTIONS = [
  { value: '',           label: 'All' },
  { value: 'Men only',   label: 'Men only' },
  { value: 'Women only', label: 'Women only' },
  { value: 'Neutral',    label: 'Neutral' },
];

const OXFORD_BADGE = {
  M:  { bg: 'bg-blue-50 text-blue-700 border-blue-200',       label: "Men's" },
  W:  { bg: 'bg-pink-50 text-pink-700 border-pink-200',       label: "Women's" },
  WC: { bg: 'bg-purple-50 text-purple-700 border-purple-200', label: "Women's w/ Children" },
  MC: { bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: "Men's w/ Children" },
};

const SOBER_BADGE = {
  'Men only':   'bg-blue-50 text-blue-700 border-blue-200',
  'Women only': 'bg-pink-50 text-pink-700 border-pink-200',
  'Neutral':    'bg-teal-50 text-teal-700 border-teal-200',
};

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    </div>
  );
}

// ─── Oxford House card ────────────────────────────────────────────────────────

function OxfordCard({ house }) {
  const badge = OXFORD_BADGE[house.gender];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-snug">
          {house.house_name || 'Oxford House'}
        </h3>
        {badge && (
          <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${badge.bg}`}>
            {badge.label}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {[house.city, house.county ? `${house.county} County` : null].filter(Boolean).join(', ')}
      </p>
      {house.meeting_phone && (
        <div className="mt-2 flex items-center gap-1.5 text-sm">
          <span className="text-gray-500 dark:text-gray-400 text-xs font-medium">House phone:</span>
          <a href={`tel:${house.meeting_phone}`} className="text-blue-600 hover:underline font-medium">{house.meeting_phone}</a>
        </div>
      )}
      {(house.contact_name || house.contact_phone) && (
        <div className="mt-1.5 flex items-center gap-1.5 text-sm">
          <span className="text-gray-500 dark:text-gray-400 text-xs font-medium">Contact:</span>
          <span className="text-gray-700 dark:text-gray-300">
            {house.contact_name || ''}
            {house.contact_name && house.contact_phone ? ' — ' : ''}
            {house.contact_phone && (
              <a href={`tel:${house.contact_phone}`} className="text-blue-600 hover:underline">{house.contact_phone}</a>
            )}
          </span>
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {house.meeting_day_time && (
          <span className="inline-flex items-center gap-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {house.meeting_day_time}
          </span>
        )}
        {house.members > 0 && (
          <span className="inline-flex items-center gap-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {house.members} members
          </span>
        )}
      </div>
      {house.notes && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">{house.notes}</p>}
    </div>
  );
}

// ─── Sober Living card ────────────────────────────────────────────────────────

function SoberCard({ home }) {
  const badgeCls = SOBER_BADGE[home.gender];
  const location = [home.street, home.city, home.zip, home.county ? `${home.county} County` : null, home.state]
    .filter(Boolean).join(', ');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-snug">{home.name}</h3>
          {home.organization && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{home.organization}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {badgeCls && (
            <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${badgeCls}`}>
              {home.gender}
            </span>
          )}
          {home.accepts_mat && (
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border bg-green-50 text-green-700 border-green-200">
              MAT friendly
            </span>
          )}
        </div>
      </div>

      {location && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{location}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-3">
        {home.phone && (
          <a href={`tel:${home.phone}`} className="text-blue-600 hover:underline text-sm font-medium">{home.phone}</a>
        )}
        {home.website && (
          <a href={home.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
            Visit website →
          </a>
        )}
        {home.total_beds > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{home.total_beds} beds</span>
        )}
        {home.baths > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{home.baths} baths</span>
        )}
      </div>

      {(home.cost || home.payment_options) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {home.cost && (
            <span className="text-xs text-gray-600 dark:text-gray-300"><span className="font-medium">Cost:</span> {home.cost}</span>
          )}
          {home.payment_options && (
            <span className="text-xs text-gray-600 dark:text-gray-300"><span className="font-medium">Payment:</span> {home.payment_options}</span>
          )}
        </div>
      )}

      {home.accreditation && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400"><span className="font-medium">Accreditation:</span> {home.accreditation}</p>
      )}

      {home.description && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{home.description}</p>
      )}
    </div>
  );
}

// ─── Shared filter bar ────────────────────────────────────────────────────────

function FilterBar({ counties, county, setCounty, genderOptions, gender, setGender }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <select
        value={county}
        onChange={(e) => setCounty(e.target.value)}
        className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <option value="">All Counties</option>
        {counties.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <div className="flex items-center gap-1">
        {genderOptions.map((o) => (
          <button
            key={o.value}
            onClick={() => setGender(o.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              gender === o.value
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {(county || gender) && (
        <button onClick={() => { setCounty(''); setGender(''); }} className="text-xs text-blue-600 hover:underline">
          Clear filters
        </button>
      )}
    </div>
  );
}

// ─── Oxford Houses panel ──────────────────────────────────────────────────────

function OxfordPanel() {
  const [houses, setHouses]     = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [county, setCounty]     = useState('');
  const [gender, setGender]     = useState('');

  useEffect(() => {
    axios.get('/api/housing/counties').then((r) => setCounties(r.data || [])).catch(() => {});
  }, []);

  const fetch = useCallback((c, g) => {
    setLoading(true);
    const params = {};
    if (c) params.county = c;
    if (g) params.gender = g;
    axios.get('/api/housing', { params })
      .then((r) => setHouses(r.data || []))
      .catch(() => setError('Could not load Oxford Houses.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetch(county, gender); }, [county, gender, fetch]);

  return (
    <>
      <FilterBar counties={counties} county={county} setCounty={setCounty}
        genderOptions={OXFORD_GENDER_OPTIONS} gender={gender} setGender={setGender} />
      {!loading && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Showing {houses.length} {houses.length === 1 ? 'house' : 'houses'}</p>
      )}
      {loading ? <Spinner /> : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : houses.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {houses.map((h) => <OxfordCard key={h.id} house={h} />)}
        </div>
      )}
    </>
  );
}

// ─── Sober Living panel ───────────────────────────────────────────────────────

function SoberPanel() {
  const [homes, setHomes]       = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [county, setCounty]     = useState('');
  const [gender, setGender]     = useState('');

  useEffect(() => {
    axios.get('/api/sober-living/counties').then((r) => setCounties(r.data || [])).catch(() => {});
  }, []);

  const fetch = useCallback((c, g) => {
    setLoading(true);
    const params = {};
    if (c) params.county = c;
    if (g) params.gender = g;
    axios.get('/api/sober-living', { params })
      .then((r) => setHomes(r.data || []))
      .catch(() => setError('Could not load sober living homes.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetch(county, gender); }, [county, gender, fetch]);

  return (
    <>
      <FilterBar counties={counties} county={county} setCounty={setCounty}
        genderOptions={SOBER_GENDER_OPTIONS} gender={gender} setGender={setGender} />
      {!loading && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Showing {homes.length} {homes.length === 1 ? 'home' : 'homes'}</p>
      )}
      {loading ? <Spinner /> : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : homes.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {homes.map((h) => <SoberCard key={h.id} home={h} />)}
        </div>
      )}
    </>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center py-20 text-center">
      <svg className="w-14 h-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
      <p className="text-gray-500 font-medium">No results match your filters</p>
      <p className="text-gray-400 text-sm mt-1">Try changing or clearing your filters</p>
    </div>
  );
}

// ─── Main Housing page ────────────────────────────────────────────────────────

const SUB_TABS = ['Oxford Houses', 'Sober Living'];

export default function Housing() {
  const [subTab, setSubTab] = useState('Oxford Houses');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>Recovery Housing | WA BHA Treatment Finder</title>
        <meta name="description" content="Search Oxford Houses and sober living homes in Washington State. Filter by county and gender. Recovery housing directory for WA behavioral health professionals." />
      </Helmet>
      <Header right={null} />
      <NavTabs />

      <div className="max-w-7xl mx-auto px-4 pt-5 pb-12">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Housing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Recovery housing directory for Washington State
          </p>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700 mb-6">
          {SUB_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setSubTab(t)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                subTab === t
                  ? 'border-blue-600 text-blue-700 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {subTab === 'Oxford Houses' && <OxfordPanel />}
        {subTab === 'Sober Living'  && <SoberPanel />}
      </div>
    </div>
  );
}
